import { useState } from "react";
import { OwnerEmailedTag, NoEmailTag } from "./OwnerPitchDialog";
import { MapPin, Phone, Mail, Globe, CreditCard, UserRound, Send, UserCheck, CalendarClock, StickyNote, Zap, Lock, BadgeCheck, ChevronDown } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { DealStageTag, DealStageButtons, DealAgeTag } from "./DealStage";
import { catLabel, platLabel, localToday, outcomeOf, postedLabel, isFresh, archiveLabel } from "../lib/constants";

export const PlatformBadge = ({ platform }) => (
  <span className="font-mono-label text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
    {platLabel(platform)}
  </span>
);



export const FreshBadge = ({ testid }) => (
  <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-500 text-white shrink-0" data-testid={testid}>
    <Zap className="h-3 w-3" />Fresh
  </span>
);

// Latest send per person: yellow "Sent to Luke · Oct 17" (reps only ever get their own "you" entry).
export const SentTags = ({ lead, size = "text-[11px]" }) => {
  const latest = {};
  (lead.sent_to_reps || []).forEach((s) => { latest[s.name] = s; });
  const day = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return Object.values(latest).map((s) => (
    <span key={s.name} className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-yellow-300 text-yellow-950 font-semibold ${size}`}
      data-testid={`lead-sent-tag-${lead.id}-${s.name === "you" ? "you" : s.name.replace(/\s+/g, "-").toLowerCase()}`}>
      <Send className="h-3 w-3" />{s.name === "you" ? `Sent to you${s.by ? ` by ${s.by}` : ""}` : `Sent to ${s.name}`} · {day(s.at)}
    </span>
  ));
};

export const SignedTag = ({ lead, size = "text-[11px]" }) => (
  <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white font-semibold ${size}`} data-testid={`lead-signed-tag-${lead.id}`}>
    <BadgeCheck className="h-3 w-3" />Signed · {new Date(lead.signed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
  </span>
);

const WorkBadges = ({ lead, teamById }) => {
  const who = teamById[lead.assigned_to]?.name;
  const due = lead.follow_up_date && lead.follow_up_date <= localToday();
  const repView = ["potential", "signed"].includes(lead.bucket);
  const notes = repView ? lead.rep_notes_count : lead.notes_count;
  if (lead.bucket !== "potential" && !lead.signed_at && !lead.deal_stage && !who && !lead.follow_up_date && !notes && !lead.sent_to_reps?.length && !lead.owner_emailed_at) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2.5 text-[11px]" data-testid={`lead-work-badges-${lead.id}`}>
      {lead.bucket === "signed" && lead.signed_at && <SignedTag lead={lead} />}
      <DealAgeTag lead={lead} />
      <DealStageTag lead={lead} />
      <SentTags lead={lead} />
      {who && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-800"><UserCheck className="h-3 w-3" />{who}</span>}
      {lead.follow_up_date && (
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${due ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700"}`}>
          <CalendarClock className="h-3 w-3" />{due ? "Follow up due " : "Follow up "}{lead.follow_up_date}
        </span>
      )}
      {notes > 0 && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid={`lead-notes-count-${lead.id}`}><StickyNote className="h-3 w-3" />{notes}{repView ? " rep" : ""}</span>}
    </div>
  );
};

const LeadRow = ({ lead, active, onSelect, checked, onCheck, teamById, selectable, onEmailRep, onStage }) => {
  const [open, setOpen] = useState(false);
  const hasActions = onEmailRep || (onStage && lead.bucket === "potential");
  return (
  <div className={`flex flex-wrap sm:flex-nowrap rounded-xl border transition-colors duration-150 ${checked ? "border-blue-600 ring-1 ring-blue-600" : active ? "border-blue-600 bg-blue-50/60" : "border-slate-200 bg-white hover:border-blue-300"} bg-white`}>
  {selectable && <div className="pl-4 pt-[18px]">
    <Checkbox checked={checked} onCheckedChange={() => onCheck(lead.id)} data-testid={`lead-select-${lead.id}`} aria-label={`Select ${lead.name}`} />
  </div>}
  <button onClick={() => onSelect(lead)} data-testid={`lead-card-item-${lead.id}`} className="flex-1 min-w-0 text-left p-4 pl-3">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
      <div className="min-w-0">
        <h3 className="font-display font-bold text-slate-900 sm:truncate leading-snug" data-testid={`lead-name-${lead.id}`}>{lead.name}</h3>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 empty:hidden whitespace-nowrap">
          {!lead.signal_date_approx && isFresh(lead.signal_date) && <FreshBadge testid={`lead-fresh-badge-${lead.id}`} />}
          {lead.owner_emailed_at ? <OwnerEmailedTag lead={lead} /> : <NoEmailTag lead={lead} />}
          {lead.bucket === "archived" && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-white shrink-0 truncate max-w-[220px]" title={lead.archive_note || ""} data-testid={`lead-archive-reason-${lead.id}`}>
              {archiveLabel(lead.archive_reason) || "Archived"}{lead.archive_note ? ` · ${lead.archive_note}` : ""}
            </span>
          )}
          {outcomeOf(lead.outcome) && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${outcomeOf(lead.outcome).badge}`} data-testid={`lead-outcome-badge-${lead.id}`}>{outcomeOf(lead.outcome).label}</span>}
          {lead.shared_with?.length > 0 && <Send className="h-3 w-3 text-slate-400 shrink-0" title="Emailed to team" />}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
          <MapPin className="h-3 w-3" />
          {[lead.address, lead.city, lead.state, lead.zip].filter(Boolean).join(", ")}
        </p>
      </div>
      <div className="flex flex-row flex-wrap items-center sm:flex-col sm:items-end gap-1.5 shrink-0">
        <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{catLabel(lead.category)}</span>
        <PlatformBadge platform={lead.source_platform} />
        <span className={`text-[11px] ${lead.signal_date ? "text-slate-500" : "text-slate-400"}`} data-testid={`lead-posted-${lead.id}`}>{postedLabel(lead.signal_date, lead.signal_date_approx)}</span>
      </div>
    </div>
    <div className={`${open ? "" : "hidden"} sm:block`} data-testid={`lead-details-${lead.id}`}>
    <p className="text-xs mt-2.5 flex items-center gap-1.5" data-testid={`lead-current-pos-${lead.id}`}>
      <CreditCard className="h-3.5 w-3.5 text-blue-600" />
      <span className="text-slate-500">Current POS:</span>
      <span className={lead.current_pos ? "font-semibold text-slate-900" : "text-slate-400"}>{lead.current_pos || "Unknown"}</span>
    </p>
    {lead.contact_name && (
      <p className="text-xs mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-testid={`lead-contact-${lead.id}`}>
        <UserRound className="h-3.5 w-3.5 text-blue-600" />
        <span className="font-semibold text-slate-900">{lead.contact_name}</span>
        {lead.contact_title && <span className="text-slate-500 whitespace-nowrap">· {lead.contact_title}</span>}
        {lead.contact_phone && <span className="text-slate-600 whitespace-nowrap">· {lead.contact_phone}</span>}
      </p>
    )}
    {lead.signal_quote && <p className="text-sm text-slate-600 mt-2 line-clamp-2 italic">"{lead.signal_quote}"</p>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-xs text-slate-600">
      {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-blue-600" />{lead.phone}</span>}
      {lead.email && <span className="flex items-center gap-1 min-w-0 break-all"><Mail className="h-3 w-3 text-blue-600" />{lead.email}</span>}
      {lead.website && <span className="flex items-center gap-1 truncate max-w-[220px]"><Globe className="h-3 w-3 text-blue-600" />{lead.website.replace(/^https?:\/\//, "")}</span>}
    </div>
    </div>
    <WorkBadges lead={lead} teamById={teamById} />
  </button>
  <div className={`w-full sm:w-auto px-4 pb-3 -mt-1 sm:mt-0 sm:p-0 sm:pr-3 sm:pt-3 flex flex-row flex-wrap justify-end sm:flex-col sm:flex-nowrap items-center sm:items-end gap-1.5 ${hasActions ? "" : "sm:hidden"}`}>
    <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`lead-expand-${lead.id}`}
      className="sm:hidden mr-auto h-8 px-2.5 rounded-full text-xs font-semibold text-blue-700 hover:bg-blue-50 flex items-center gap-1 transition-colors">
      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Less" : "More details"}
    </button>
    {onStage && lead.bucket === "potential" && lead.potential_uid && <DealStageButtons compact lead={lead} onChanged={onStage} />}
    {onEmailRep && <button onClick={() => onEmailRep(lead)} title="Email to rep" aria-label={`Email ${lead.name} to a rep`} data-testid={`lead-email-rep-${lead.id}`}
      className="h-8 px-2.5 rounded-full border border-slate-200 text-xs font-semibold text-blue-700 hover:bg-blue-700 hover:text-white hover:border-blue-700 transition-colors flex items-center gap-1">
      <Send className="h-3.5 w-3.5" /><span className="hidden sm:inline">Email rep</span>
    </button>}
  </div>
  </div>
  );
};

const LockedRow = ({ lead }) => (
  <div className="relative rounded-xl bg-slate-900 p-4 overflow-hidden select-none" data-testid={`lead-locked-${lead.id}`}>
    <div className="space-y-2 blur-[3px] opacity-40" aria-hidden>
      <div className="h-4 w-1/2 rounded bg-slate-500" />
      <div className="h-3 w-3/4 rounded bg-slate-600" />
      <div className="h-3 w-2/3 rounded bg-slate-600" />
    </div>
    <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm font-semibold text-white">
      <Lock className="h-4 w-4" />Locked in demo · {catLabel(lead.category)} · {lead.state}
    </div>
  </div>
);

export const LeadList = ({ leads, activeId, onSelect, loading, selected, onCheck, teamById, selectable = true, onEmailRep, emptyText, groupBy, onStage }) => {
  if (loading) return <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-xl bg-white border border-slate-200 animate-pulse" />)}</div>;
  if (!leads.length) return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="leads-empty">
      <p className="font-display font-bold text-slate-800">No leads match yet</p>
      <p className="text-sm text-slate-500 mt-1">{emptyText || "Run a scan or loosen your filters."}</p>
    </div>
  );
  const row = (l) => l.locked ? <LockedRow key={l.id} lead={l} /> : <LeadRow key={l.id} lead={l} active={l.id === activeId} onSelect={onSelect} checked={selected.includes(l.id)} onCheck={onCheck} teamById={teamById} selectable={selectable} onEmailRep={l.view_only ? undefined : onEmailRep} onStage={onStage} />;
  if (groupBy) {  // e.g. Jeff's Potential: rep, then that rep's leads
    const groups = leads.reduce((acc, l) => ({ ...acc, [groupBy(l)]: [...(acc[groupBy(l)] || []), l] }), {});
    return (
      <div className="space-y-6" data-testid="lead-list">
        {Object.keys(groups).sort().map((name) => (
          <div key={name} className="space-y-3" data-testid={`lead-group-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">{name}
              <span className="text-xs font-semibold rounded-full bg-yellow-100 text-yellow-900 px-2 py-0.5">{groups[name].length}</span></h3>
            {groups[name].map(row)}
          </div>
        ))}
      </div>
    );
  }
  return <div className="space-y-3" data-testid="lead-list">{leads.map(row)}</div>;
};
